import React, { useState, useCallback } from "react";
import "./PostChallenge.scss";
import axios from "axios";
import { useSelector } from "react-redux";

const PostChallenge = ({ post, onChallengeStateChange }) => {
    const [challengeState, setChallengeState] = useState({
        isJoined: post?.data?.is_joined,
        isLoading: false
    });

    const projectInfo = useSelector((state) => state.projectInfo);

    const handleJoinChallenge = useCallback(async () => {
        setChallengeState(prev => ({ ...prev, isLoading: true }));
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/challenge/join`,
                { challenge_id: post?.data?.id },
                {
                    headers: {
                        pubid: projectInfo?.pubid,
                        channelid: projectInfo?.channelid,
                        uid: projectInfo?.uid,
                        'dev_id': projectInfo?.dev_id,
                        "access-token": projectInfo?.accessToken,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            setChallengeState(prev => ({ 
                ...prev, 
                isJoined: true, 
                isLoading: false 
            }));
            
            if (onChallengeStateChange) {
                onChallengeStateChange({ isJoined: true });
            }
        } catch (error) {
            console.error('Error joining challenge:', error);
            setChallengeState(prev => ({ ...prev, isLoading: false }));
        }
    }, [onChallengeStateChange, projectInfo]);

    const handleLeaveChallenge = useCallback(async () => {
        setChallengeState(prev => ({ ...prev, isLoading: true }));
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/challenge/leave`,
                { challenge_id: post?.data?.id },
                {
                    headers: {
                        pubid: projectInfo?.pubid,
                        channelid: projectInfo?.channelid,
                        uid: projectInfo?.uid,
                        'dev_id': projectInfo?.dev_id,
                        "access-token": projectInfo?.accessToken,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            setChallengeState(prev => ({ 
                ...prev, 
                isJoined: false, 
                isLoading: false 
            }));
            
            if (onChallengeStateChange) {
                onChallengeStateChange({ isJoined: false });
            }
        } catch (error) {
            console.error('Error leaving challenge:', error);
            setChallengeState(prev => ({ ...prev, isLoading: false }));
        }
    }, [onChallengeStateChange, projectInfo]);

    const handleStartChallenge = useCallback(() => {
        const parsedUrl = new URL(window.location.href);
        const vanityUrl = post?.data?.vanity_url;
        if (vanityUrl) {
            const redirectedUrl = `${parsedUrl.origin}/show-details/${vanityUrl}`;
            window.open(redirectedUrl, "_blank");
        }
    }, [post]);

    const challengeInfo = post?.data?.challenge_info || "";
    const challengeDate = post?.data?.date || "";
    const thumbnailUrl = post?.media?.[0]?.url;
    
    const getBadgeDate = () => {
        const MONTHS = [
            'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
            'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
        ];

        const parseDateParts = (s) => {
            if (!s) return null;
            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (m) {
                const monthIdx = parseInt(m[2], 10) - 1;
                const dayNum = parseInt(m[3], 10);
                if (monthIdx >= 0 && monthIdx <= 11) {
                    return { month: MONTHS[monthIdx], day: dayNum };
                }
            }

            const d = new Date(s);
            if (!isNaN(d.getTime())) {
                return { month: MONTHS[d.getUTCMonth()], day: d.getUTCDate() };
            }

            return null;
        };

        const startDateString = post?.data?.start_date;
        if (startDateString) {
            try {
                const parsed = parseDateParts(startDateString);
                if (parsed) return parsed;
            } catch (error) {
                console.log('Error parsing start_date:', error);
            }
        }
        
        const dateString = post?.data?.date;
        if (dateString && dateString !== " - , ") {
            try {
                const parsed = parseDateParts(dateString);
                if (parsed) return parsed;
            } catch (error) {
                console.log('Error parsing date:', error);
            }
        }
        
        const fallbackDate = new Date(post?.created_at || Date.now());
        return {
            month: MONTHS[fallbackDate.getUTCMonth()],
            day: fallbackDate.getUTCDate()
        };
    };

    const badgeDate = getBadgeDate();

    return (
        <div className="post-challenge">
            <div className="challenge-container">
                <div className="mobile-top-row">
                    <div className="challenge-calendar">
                        <div className="calendar-header">{badgeDate.month}</div>
                        <div className="calendar-day">{badgeDate.day}</div>
                    </div>
                    {challengeDate && (
                        <div className="mobile-challenge-date">{challengeDate}</div>
                    )}
                </div>
                
                <div className="desktop-calendar challenge-calendar">
                    <div className="calendar-header">{badgeDate.month}</div>
                    <div className="calendar-day">{badgeDate.day}</div>
                </div>
                
                <div className="challenge-details">
                    <div className="challenge-info">
                        <div className="challenge-header">
                            {!challengeState.isJoined && (
                                <div className="challenge-title">
                                    <span className="title-text">Join challenge to see content</span>
                                    <span className="title-bullet"> • </span>
                                </div>
                            )}
                            {challengeDate && (
                                <div className="challenge-date">{challengeDate}</div>
                            )}
                        </div>
                        <div className="challenge-text">{challengeInfo}</div>
                    </div>
                    <div className="challenge-actions">
                        {!post?.data?.challenge_ended && (
                            <>
                                {!challengeState.isJoined ? (
                                    <button 
                                        className="join-btn"
                                        onClick={handleJoinChallenge}
                                        disabled={challengeState.isLoading}
                                    >
                                        {challengeState.isLoading ? "Joining..." : "Join"}
                                    </button>
                                ) : (
                                    <div className="joined-actions">
                                        <button 
                                            className="start-btn"
                                            onClick={handleStartChallenge}
                                            disabled={challengeState.isLoading}
                                        >
                                            Start Challenge
                                        </button>
                                        <button 
                                            className="leave-btn"
                                            onClick={handleLeaveChallenge}
                                            disabled={challengeState.isLoading}
                                        >
                                            {challengeState.isLoading ? "Leaving..." : "Leave"}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PostChallenge;